import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Moon, BarChart3, Shield, Clock, ChevronRight, Star } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_16338d16-af9f-4ec6-a8f3-cd6da42efe59/artifacts/7xtaj2mx_WhatsApp%20Image%202026-03-02%20at%203.01.19%20PM%20%281%29.jpeg";
const HERO_BG = "https://images.unsplash.com/photo-1762199904138-d163fe89540a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxwZWFjZWZ1bCUyMHNsZWVwJTIwYmVkcm9vbSUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzcyNjA1Mjg2fDA&ixlib=rb-4.1.0&q=85";
const MEDICAL_TEAM = "https://images.unsplash.com/photo-1758691463203-cce9d415b2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxkb2N0b3IlMjBjb25zdWx0YXRpb24lMjB0YWJsZXQlMjBtZWRpY2FsfGVufDB8fHx8MTc3MjYwNTI4N3ww&ixlib=rb-4.1.0&q=85";

const LandingPage = () => {
  const features = [
    {
      icon: Moon,
      title: "Track Your Sleep",
      description: "Log your daily sleep patterns with our comprehensive 14-day diary following clinical standards."
    },
    {
      icon: BarChart3,
      title: "Analyze Patterns",
      description: "Get instant calculations of sleep efficiency, total sleep time, and quality metrics."
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Your sleep data is encrypted and securely stored. You own your data completely."
    },
    {
      icon: Clock,
      title: "14-Day Program",
      description: "Follow the recommended two-week tracking period for accurate sleep pattern analysis."
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src={LOGO_URL} alt="SleepMed" className="h-10 w-auto" />
              <span className="font-heading font-bold text-xl text-slate-900">SleepMediary</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600 hover:text-primary" data-testid="login-nav-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-primary hover:bg-primary/90" data-testid="register-nav-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 hero-gradient opacity-50"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
                <Star className="h-4 w-4" />
                Clinical-Grade Sleep Tracking
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6">
                Two-Week Sleep Diary
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                Track your sleep patterns with our professional sleep diary. 
                Fill one form every day for 14 days to help your doctor understand 
                your sleep health. Answer honestly - there are no right or wrong answers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="btn-primary w-full sm:w-auto group" data-testid="get-started-btn">
                    Start Your Diary
                    <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="btn-secondary w-full sm:w-auto" data-testid="sign-in-btn">
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative animate-fadeIn animate-delay-200 hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-primary/20">
                <img 
                  src={MEDICAL_TEAM} 
                  alt="Medical consultation" 
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
              </div>
              
              {/* Floating metric card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 animate-slideIn animate-delay-400">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <BarChart3 className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Sleep Efficiency</p>
                    <p className="text-2xl font-bold text-green-600">92%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Why Track Your Sleep?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Understanding your sleep patterns is the first step to improving your sleep health.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="card-base hover-lift animate-fadeIn"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Understand Your Sleep?
          </h2>
          <p className="text-lg text-primary-100 mb-8 opacity-90">
            Start tracking today and take control of your sleep health.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-primary hover:bg-slate-100" data-testid="cta-start-btn">
              Create Free Account
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="SleepMed" className="h-8 w-auto brightness-0 invert" />
              <span className="text-slate-400 text-sm">Laboratory · Solutions · Education</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2026 SleepMed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
